// All copy and structured data for the HEIR investor deal room.
import {
    Sparkles,
    ShieldCheck,
    Scale,
    HeartPulse,
    Users,
    Umbrella,
    LineChart,
    Landmark,
    Calculator,
    Building2,
    Home,
} from "lucide-react";

export const CONTACT = {
    email: "investors@h3ir.com",
    mailto:
        "mailto:investors@h3ir.com?subject=HEIR%20Seed%20%E2%80%94%20Investor%20Inquiry&body=Hello%20HEIR%20team%2C%0A%0AI%20would%20like%20to%20learn%20more%20about%20the%20HEIR%20Seed%20round.%0A%0A",
};

export const NAV_LINKS = [
    { id: "thesis", label: "Thesis" },
    { id: "deal", label: "The Deal" },
    { id: "funds", label: "Use of Funds" },
    { id: "traction", label: "Traction" },
    { id: "product", label: "Product" },
    { id: "distribution", label: "Distribution" },
    { id: "roadmap", label: "Roadmap" },
    { id: "market", label: "Market" },
    { id: "team", label: "Team" },
    { id: "dataroom", label: "Data Room" },
    { id: "invest", label: "Invest" },
];

export const METRICS = [
    { label: "Round", value: "Seed", sub: "SAFE" },
    { label: "Valuation", value: "$10M", sub: "Post" },
    { label: "Raising", value: "$1M", sub: "Round Size" },
    { label: "Minimum", value: "$50K", sub: "Per Investor" },
];

export const DEAL_TERMS = [
    { term: "Round", detail: "Seed" },
    { term: "Instrument", detail: "SAFE (Simple Agreement for Future Equity)" },
    { term: "Valuation", detail: "$10,000,000" },
    { term: "Round Size", detail: "$1,000,000" },
    { term: "Minimum Investment", detail: "$50,000" },
    { term: "Eligibility", detail: "Qualified / Accredited investors preferred" },
    { term: "Offering Type", detail: "Private placement (Singapore entity)" },
];

export const USE_OF_FUNDS = [
    {
        category: "Acquisition & Growth",
        allocation: "40%",
        amount: "$400,000",
        purpose:
            "Channels and campaigns to reach people who have been putting off estate planning, driving volume through the $88 entry.",
    },
    {
        category: "Product Experience",
        allocation: "30%",
        amount: "$300,000",
        purpose:
            "Perfect the guided AI conversation, plan generation, vault conversion flows, and overall user experience.",
    },
    {
        category: "Team",
        allocation: "20%",
        amount: "$200,000",
        purpose:
            "Key growth, product, and operational talent required to hit the volume target.",
    },
    {
        category: "Operations & Buffer",
        allocation: "10%",
        amount: "$100,000",
        purpose:
            "Compliance, legal, infrastructure, and runway contingency.",
    },
];

export const TRACTION = [
    {
        title: "Company Registered",
        body: "HEIR PTE. LTD. incorporated in Singapore and in good standing.",
    },
    {
        title: "Banking Live",
        body: "Bank account in place with payment processing enabled.",
    },
    {
        title: "Legacy Interview Functioning",
        body: "First revenue generated from private invitations. Every user provides feedback and the interview is improved daily.",
    },
    {
        title: "Specialist Certification",
        body: "The HEIR Specialist Certification program is live. Completing certification unlocks a higher revenue share on sales.",
    },
    {
        title: "Referral Program",
        body: "Referral tracking and payouts are running end-to-end.",
    },
    {
        title: "Vertical Landing Pages",
        body: "Dedicated landing pages live for every priority vertical.",
    },
    {
        title: "Advisory Board Growing",
        body: "Adding attorneys, GTM specialists, channel partners, and strategy & capital-formation advisors.",
    },
    {
        title: "Development Velocity",
        body: "Feature cycles measured in hours, not days or weeks.",
    },
    {
        title: "Phase 2 & 3 Optionality",
        body: "Months of additional features are already built and turned off, ready to switch on once Phase 1 PMF is locked.",
    },
    {
        title: "Bulk & Partner Dashboard",
        body: "Bulk purchasing flow and partner dashboard for distributing interviews are complete and functioning.",
    },
];

export const CAPABILITIES = [
    {
        icon: Sparkles,
        title: "AI-Guided Entry ($88)",
        body: "A simple, conversational interview that captures the essential information and generates a complete starting plan.",
    },
    {
        icon: ShieldCheck,
        title: "Self-Custody Execution",
        body: "Smart contracts and vaults that keep the user in control of their assets.",
    },
    {
        icon: Scale,
        title: "Legal Layer",
        body: "Jurisdiction-aware documents that respect real-world legal and cultural requirements.",
    },
    {
        icon: HeartPulse,
        title: "Proof of Life & Automatic Execution",
        body: "Systems that maintain user control during life and execute wishes when the time comes.",
    },
    {
        icon: Users,
        title: "Generational Relationship",
        body: "Ongoing updates, family onboarding, and expansion of the plan over decades rather than a one-time transaction.",
    },
];

export const B2B_CHANNELS = [
    { icon: Building2, label: "Mid-Market Employers (50–500 Employees)", priority: true },
    { icon: Scale, label: "Estate Planning & Elder Law Attorneys", priority: true },
    { icon: Umbrella, label: "Life Insurance Agents & Carriers", priority: true },
    { icon: Home, label: "Real Estate Brokers & Agencies", priority: true },
    { icon: LineChart, label: "Financial Advisors, RIAs & Wealth Managers" },
    { icon: Users, label: "Employee Benefits Brokers & HR Platforms" },
    { icon: Landmark, label: "Banks, Credit Unions & Private Banking" },
    { icon: Calculator, label: "CPAs and Tax Advisors" },
];

export const ROADMAP = [
    {
        phase: "01",
        period: "MVP & Product-Market Fit",
        title: "Prove the Model, Prove the Motion",
        points: [
            "Finalize the $88 guided experience and lock unit economics",
            "Execute a focused B2B2C go-to-market through priority verticals: mid-market employers (50–500), estate attorneys, insurance brokers, and real estate brokers",
            "Sell bulk licenses that let partners deliver the $88 entry inside existing client relationships",
            "Reach $2M in revenue and validate conversion into deeper vault and succession usage",
            "Establish clear retention, expansion, and unit-economics signals for the next round",
        ],
    },
    {
        phase: "02",
        period: "Agentic Estate Companion",
        title: "Subscription Revenue at Scale",
        points: [
            "Evolve Arthur into an always-on agentic companion that helps every household set up, update, and manage their estate",
            "Move from a one-time $88 entry into a recurring subscription with meaningful ARPU and long retention",
            "Deepen vault, family onboarding, and succession features that make the ongoing relationship indispensable",
            "Broaden distribution across financial advisors, banks, benefits platforms, and family offices",
            "Establish HEIR as the default operating system for household legacy",
        ],
    },
    {
        phase: "03",
        period: "Agentic Trustees & Generational Custody",
        title: "Infrastructure for Multi-Generational Wealth",
        points: [
            "Introduce agentic trustees that administer irrevocable trusts on behalf of families",
            "Secure, allocate, and steward assets across decades and generations with cryptographic assurance",
            "Serve as the settlement layer between families, professionals, and institutions of trust",
            "Open institutional and professional channels for HEIR-native trust products",
            "Cement HEIR as core infrastructure for the largest wealth transfer in history",
        ],
    },
];

export const TEAM = [
    {
        name: "Logan Golema",
        role: "Founder & Chairman",
        img: "/people/logan.png",
        bioLead:
            "Operator first. Builder of the infrastructure others later call inevitable.",
        bio: [
            "**Logan Ryan Golema** is a rare breed of operator who has spent fifteen years building at the bleeding edge of autonomous AI agents, decentralized infrastructure, and defense-grade hard technology, not as a passive allocator, but as the person who actually ships the systems the rest of the market later funds.",
            "He started absurdly early. A youth coder and app developer who became a Bitcoin adopter in 2009, before he was old enough to open a traditional bank account. That head start compounded into multiple blockchain exits and a parallel track advising and structuring token launches, where he has facilitated over $50 million in TGE raises. The result is hard-won, pattern-level judgment on what actually separates durable crypto-native businesses from the ones that only look good in a bull market.",
            "His defining technical credential is **ElizaOS**. As a cofounding engineer of what became the world's most widely adopted open-source AI agent framework, Logan helped architect the system and scale it from first commits to a **$2.5 billion market capitalization**. He didn't just watch the agentic category form. He helped write the reference standard for how autonomous agents are built, deployed, and given real economic agency onchain. That experience is now the lens he brings to every agentic investment: he has already shipped the infrastructure layer the entire category is being built on.",
            "Today he is Technical Founder and CEO of **Heir**, bringing estate planning onchain so intergenerational family wealth actually reaches the people it was meant for: cleanly, transparently, and without the traditional friction and opacity.",
            "His hard-technology credentials are equally rare. At **Lunargistics Corporation**, which he founded and led as Founding Engineer and CEO, he built and sold blockchain-secured logistics, space systems, and mission infrastructure directly into the Department of War. He ran the full procurement lifecycle, from prototype contracting and program-office relationships through compliance-hardened deployment, for a customer where auditability, supply-chain provenance, and cryptographic assurance are non-negotiable operational requirements. That experience gave him genuine fluency in defense sales cycles, government-grade security posture, and the unforgiving realities of shipping dual-use technology.",
            "He also founded and operated **Galaxer**, a spatial gaming platform for Apple Vision Pro that fused immersive AR worlds with onchain economies, placing him among the earliest operators in spatial computing before the category even had a real market.",
            "Logan's record stretches further: he served as Chief Technology Officer of Alpha Compute Corp (alphacompute.ai, formerly AlphaTON Capital, Nasdaq: ATON), leading technical strategy through the company's transition into compute infrastructure. He founded and led Sentience (focused on thinking, feeling agents), acted as Observer CTO at Examine, and led operations at Partisia Blockchain, a privacy-preserving blockchain infrastructure company.",
            "He is a frequent speaker on agentic systems and decentralization: Consensus Hong Kong 2025 (\"Agentic Freedom and Compute for All\"), Web Summit Lisbon, Blockchain Expo Europe, IoT Tech Expo Europe, CoinAgenda, Blockchain Futurist, and has delivered a TED talk on agentic personhood and equality of opportunity for autonomous systems.",
            "Before any of it, Logan spent six years competing at the highest level of Major League Gaming in Halo. An early education in performance under pressure that still shapes how he evaluates founders, and how he builds.",
        ],
    },
    {
        name: "Kyle Kemper",
        role: "President",
        img: "/people/kyle.png",
    },
    {
        name: "Blake Wilkinson",
        role: "Business Officer",
        img: "/people/blake.png",
    },
];

export const ADVISORS = [
    { name: "Bobby Kennedy III", img: "/people/kennedy.png" },
    { name: "Tracy Leparulo", img: "/people/tracy.png" },
    {
        name: "Joseph Matarase",
        img: "/people/matarase.png",
    },
    { name: "Melinda Rockwell", img: "/people/melinda.png" },
    { name: "Jeremy Price", img: "/people/jeremy.png" },
    { name: "David Gubin", img: "/people/gubin.png" },
    { name: "Bobby Bailey", img: "/people/bailey.png" },
    { name: "Trevor Ogata", img: "/people/ogata.png" },
    { name: "John Scianna", img: "/people/scianna.png" },
];

export const DATA_ROOM = [
    {
        category: "Corporate & Legal",
        items: [
            "Certificate of Incorporation (HEIR PTE. LTD., Singapore)",
            "Company summary and constitutional documents",
            "Cap table summary",
            "SAFE template and subscription documents",
            "Key governance and equity agreements",
        ],
    },
    {
        category: "Product & Technology",
        items: [
            "Product overview and feature list",
            "$88 guided experience description and status",
            "High-level architecture and self-custody approach",
            "Legal document engine overview",
        ],
    },
    {
        category: "Market & Traction",
        items: [
            "Market research on will ownership and planning delay",
            "Volume and conversion targets",
            "Roadmap and 12-month milestones",
        ],
    },
    {
        category: "Financial",
        items: [
            "Use of Funds breakdown",
            "High-level financial assumptions and runway",
            "Unit economics framework for the $88 entry and LTV expansion",
        ],
    },
    {
        category: "Team & Advisors",
        items: ["Leadership and advisor bios (to be expanded)"],
    },
];

export const INVEST_STEPS = [
    "Confirm interest and qualified / accredited investor status as applicable",
    "Request access to the full data room and SAFE documentation",
    "Review materials and schedule a call if desired",
    "Complete subscription documents",
    "Wire funds per instructions provided",
];

export const IMAGES = {
    goldBg:
        "https://images.unsplash.com/photo-1761437856376-2ce1c483343b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDF8MHwxfHNlYXJjaHwxfHxwcmVtaXVtJTIwZGFyayUyMGx1eHVyeSUyMGFic3RyYWN0JTIwZ29sZCUyMGJhY2tncm91bmR8ZW58MHx8fHwxNzgyODA3ODc2fDA&ixlib=rb-4.1.0&q=85",
    product: "/product.png",
};
